import React, { useState } from 'react'
import { Handle, Position, NodeProps } from 'reactflow'
import { Users, Settings, Brain, PenTool, BarChart3 } from 'lucide-react'

interface AgentNodeData {
  label: string
  config: {
    role: string
    goal: string
    backstory: string
    model: string
    tools: string[]
  }
}

const AgentNode: React.FC<NodeProps<AgentNodeData>> = ({ data, selected }) => {
  const [showConfig, setShowConfig] = useState(false)
  
  const getAgentIcon = (label: string) => {
    if (label.toLowerCase().includes('research')) return Users
    if (label.toLowerCase().includes('writer')) return PenTool
    if (label.toLowerCase().includes('analyst')) return BarChart3
    return Brain
  }
  
  const Icon = getAgentIcon(data.label)

  const handleDoubleClick = () => {
    setShowConfig(true)
  }

  return (
    <>
      <div 
        className={`node-base node-agent w-48 h-20 ${selected ? 'ring-2 ring-blue-400' : ''}`}
        onDoubleClick={handleDoubleClick}
      >
        <div className="flex items-center h-full p-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center mr-3" style={{backgroundColor: 'var(--color-node-agent-bg)'}}>
            <Icon className="w-5 h-5" style={{color: 'var(--color-node-agent)'}} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900 dark:text-white text-sm truncate">
              {data.label}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {data.config.role}
            </div>
          </div>
          <button
            onClick={() => setShowConfig(true)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <Settings className="w-4 h-4" />
          </button>
        </div>
        
        {/* Input handle */}
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 bg-white border-2 border-blue-600"
        />
        
        {/* Output handle */}
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 bg-white border-2 border-blue-600"
        />
      </div>
      
      {/* Configuration Modal */}
      {showConfig && (
        <div className="fixed inset-0 z-50" key="agent-modal">
          <div 
            className="absolute bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 max-h-[85vh] overflow-hidden" 
            style={{
              width: '500px',
              minWidth: '500px',
              maxWidth: '500px',
              left: '350px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                Agent
              </h3>
              <button
                onClick={() => setShowConfig(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 text-xl font-light"
              >
                ×
              </button>
            </div>
            
            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Role
                  </label>
                  <input
                    type="text"
                    value={data.config.role}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Senior Research Analyst"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Goal
                  </label>
                  <textarea
                    value={data.config.goal}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="What is this agent trying to achieve?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Backstory
                  </label>
                  <textarea
                    value={data.config.backstory}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    placeholder="Background context for this agent"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Model
                  </label>
                  <select 
                    value={data.config.model} 
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="gpt-4">GPT-4</option>
                    <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="claude-3">Claude 3</option>
                    <option value="gemini-pro">Gemini Pro</option>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
              <button
                onClick={() => setShowConfig(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => setShowConfig(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AgentNode
